import { all, call, put, select, takeLatest, takeEvery } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import { isNil, noop } from 'lodash';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { loanActions } from '../actions/loan.actions';
import { errorActions } from '../actions';
import { createFilterActions } from '../actions/filter.actions';
import { BwicFilterType, CommonFilter, TFilter } from '../types/filters/FilterState';
import { FilterSelectGroup, FilterBooleanGroup, FilterDateGroup, FilterDateTimeGroup, FilterRadioGroup } from '../types/filters/FilterGroup';
import { AppState } from '../types/state/AppState';
import { LoanCurrency } from '../types/loan/LoanCurrency';
import { LoanStatus } from '../types/loan/LoanStatus';
import { LoanReviewStatus } from '../types/loan/LoanReviewStatus';
import { Rating } from '../types/enums/Rating';
import { LoanRefIndex } from '../types/enums/loan/LoanRefIndex';
import { QueryStringArgumentSerializer } from '../utils/filtering/serializers/QueryStringArgumentSerializer';
import {
    arrangers,
    loanDataroom,
    loanDueFrom,
    loanDueTo,
    loanRefIndex,
    dateFrom,
    dateTo,
    loanAnalysts,
    loanCovLite,
    loanIndustries,
    loanUseOfProceeds,
    loanIssuers,
    loanReviewStatuses,
    loanStatuses,
    numericRange,
    loanCurrencies,
    ratings,
} from '../utils/filtering/serializers/serializer.definitions';
import { queryStringSerializer } from '../utils/filtering/query-string-serializer';
import { DeserializeCommandHandler } from '../utils/filtering/serializers/DeserializeCommand';
import { history } from '../history';
import { LocationState, LocationStateBuilder } from '../types/state/ui/LocationState';
import { roles, routes } from '../constants';
import { convertToApiCriteria, getDateOptionByRange, getDateTimeOptionByRange, getTimeOptionsFromRange, getYearsRange } from '../utils/filtering/filter.utils';
import { dateRangeFilterOptions } from '../constants/date-range.filter';
import { DateTimeRange } from '../types/filters/DateTimeRange';

import { loanService, LoanSearchParams } from '../services/loan.service';
import { cloManagerUsersActions } from '../actions/entities/cloManagerUsers.actions';
import { collateralManagersActions } from '../actions/entities/collateralManagers.action';
import { debtInvestorsActions } from '../actions/entities/debtInvestors.action';
import { arrangersActions } from '../actions/entities/arrangers.actions';
import { loanFieldValuesActions } from '../actions/entities/loanFieldValues.actions';
import { LoanField } from '../types/loan/LoanField';
import { LoanFieldValues } from '../types/loan/LoanFieldValues';
import { Company } from '../types/amr-pipeline/models/Company';
import { Loan } from '../types/loan/Loan';
import { LoanState } from '../types/state/LoanState';
import { PaginationResult } from '../types/PaginationResult';
import { isRequesting, isRequestSuccess } from '../utils';
import { ListBuilder } from '../utils/ListBuilder';
import { user } from '../user/user';
import { SubscriptionFeature } from '../types/billing/SubscriptionFeature';
import { PageConfigType } from '../types/page-config/PageConfigType';
import { UserConfigFilter } from '../types/user-config/UserConfigFilter';

const filterActions = createFilterActions(BwicFilterType.LoanMonitor);

function getFilterParams(filter: CommonFilter): LoanSearchParams {
    const defaultDateRange = { dateFrom: undefined, dateTo: undefined };
    const isCollateralManager = user.hasRoles(roles.CollateralManager);

    const selectedFrom = <T>(key: string) =>
        (filter[key] as FilterSelectGroup<T>)?.filter.filter(f => f.selected).map(f => f.value as T) ?? [];

    const statuses = selectedFrom<LoanStatus>('statuses');
    const review = isCollateralManager ? selectedFrom<LoanReviewStatus>('review') : [];
    const analyst = isCollateralManager ? selectedFrom<string>('analyst') : [];
    const currencies = selectedFrom<LoanCurrency>('currency');
    const arrangerValues = selectedFrom<string>('arranger');
    const coArrangers = selectedFrom<string>('coArranger');
    const industries = selectedFrom<string>('industry');
    const usesOfProceeds = selectedFrom<string>('useOfProceeds');
    const dataroom = selectedFrom<string>('dataroom');

    const covLite = (filter.covLite as FilterBooleanGroup)?.filter.selectedOption ?? undefined;
    const refIndex = (filter.refIndex as FilterRadioGroup<LoanRefIndex>)?.filter.selectedOption ?? undefined;

    const pricingFilter = filter.pricing as FilterDateGroup;
    const pricingRange = pricingFilter?.filter.selectedOption
        ? convertToApiCriteria(pricingFilter.filter.selectedOption, pricingFilter.filter.options)
        : defaultDateRange;

    const maturityFilter = filter.maturity as FilterDateGroup;
    const maturityRange = maturityFilter?.filter.selectedOption
        ? convertToApiCriteria(maturityFilter.filter.selectedOption, maturityFilter.filter.options)
        : defaultDateRange;

    const dueFilter = filter.due as FilterDateTimeGroup;
    const dueFrom = dueFilter?.filter.selectedOption?.from?.date;
    const dueTo = dueFilter?.filter.selectedOption?.to?.date;

    const termFilter = filter.term as FilterDateGroup;
    const termYears = getYearsRange(termFilter);
    const ratingsValues = selectedFrom<Rating>('ratings');
    const cfRatingsValues = selectedFrom<Rating>('cfRatings');

    return {
        status: statuses.length ? statuses : undefined,
        review: review.length ? review : undefined,
        analystUserId: analyst.length ? analyst.map(Number) : undefined,
        currencyCode: currencies.length ? currencies : undefined,
        arrangerReferenceNames: arrangerValues.length ? arrangerValues : undefined,
        coArrangerReferenceNames: coArrangers.length ? coArrangers : undefined,
        industries: industries.length ? industries : undefined,
        useOfProceeds: usesOfProceeds.length ? usesOfProceeds : undefined,
        dataroom: dataroom.length ? dataroom : undefined,
        covLite: !isNil(covLite) ? covLite as boolean : undefined,
        refIndex: refIndex ? [refIndex as LoanRefIndex] : undefined,
        pricingDateFrom: pricingRange.dateFrom,
        pricingDateTo: pricingRange.dateTo,
        maturityFrom: maturityRange.dateFrom,
        maturityTo: maturityRange.dateTo,
        dueDateFrom: dueFrom ? moment(dueFrom).utc(true).toISOString() : undefined,
        dueDateTo: dueTo ? moment(dueTo).utc(true).toISOString() : undefined,
        termFrom: termYears?.from,
        termTo: termYears?.to,
        ratings: ratingsValues.length ? ratingsValues : undefined,
        cfRatings: cfRatingsValues.length ? cfRatingsValues : undefined,
    };
}

function* watchRestoreFiltersQueryString() {
    if (!history.location.search) {
        yield redirectWithFilterArguments();
    }

    yield put(loanActions.setShouldRestoreFiltersQueryString(false));
}

function* redirectWithFilterArguments() {
    const filter: CommonFilter = yield select((s: AppState) => s.filters.loanMonitor.filter);
    const issuersSearch: string[] = yield select((s: AppState) => s.loan.issuersSearch);
    const isCollateralManager = user.hasRoles(roles.CollateralManager);

    const params = getFilterParams(filter);
    const { pricingDateFrom, pricingDateTo, maturityFrom, maturityTo, termFrom, termTo } = params;

    const dueFilter = filter.due as FilterDateTimeGroup;
    const dueFromDate = dueFilter?.filter.selectedOption?.from?.date;
    const dueToDate = dueFilter?.filter.selectedOption?.to?.date;

    const [termSerializers] = numericRange('termFrom', 'termTo');

    const serializers: QueryStringArgumentSerializer<any>[] = [
        loanStatuses(noop),
        loanCurrencies(noop),
        arrangers(noop),
        arrangers(noop, undefined, 'coArranger'),
        dateFrom(noop, 'pricingFrom'),
        dateTo(noop, 'pricingTo'),
        loanCovLite(noop),
        loanDataroom(noop),
        loanIndustries(noop),
        loanUseOfProceeds(noop),
        loanIssuers(noop),
        dateFrom(noop, 'maturityFrom'),
        dateTo(noop, 'maturityTo'),
        loanRefIndex(noop),
        ...termSerializers,
        loanDueFrom(noop),
        loanDueTo(noop),
        ratings(noop),
        ratings(noop, undefined, 'cfRatings'),
        ...(isCollateralManager ? [loanReviewStatuses(noop), loanAnalysts(noop)] : []),
    ];

    const criteria: Record<string, any> = {
        statuses: params.status,
        currency: params.currencyCode,
        arrangers: params.arrangerReferenceNames,
        coArranger: params.coArrangerReferenceNames,
        pricingFrom: pricingDateFrom,
        pricingTo: pricingDateTo,
        covLite: params.covLite,
        dataroom: params.dataroom,
        industry: params.industries,
        useOfProceeds: params.useOfProceeds,
        issuer: issuersSearch.length ? issuersSearch : undefined,
        maturityFrom,
        maturityTo,
        refIndex: params.refIndex?.[0],
        termFrom,
        termTo,
        dueFrom: dueFromDate,
        dueTo: dueToDate,
        ratings: params.ratings,
        cfRatings: params.cfRatings,
        ...(isCollateralManager ? { review: params.review, analyst: params.analystUserId } : {}),
    };

    const queryString = queryStringSerializer.serialize(criteria, serializers);

    if (queryString) {
        yield call(history.replace, history.location.pathname + '?' + queryString);
    } else if (history.location.search) {
        yield call(history.replace, history.location.pathname);
    }

    yield put(loanActions.setShouldRestoreFiltersQueryString(false));
}

function* watchInit() {
    try {
        const locationSearchString: string = history.location.search;
        const searchString = locationSearchString?.trim();

        const initialFilter: TFilter<BwicFilterType.LoanMonitor> = yield select((s: AppState) => s.filters.loanMonitor.initialFilter);
        if (!initialFilter) {
            const filter: TFilter<BwicFilterType.LoanMonitor> = yield select((s: AppState) => s.filters.loanMonitor.filter);
            yield put(filterActions.init({ ...filter }));
        }

        const hasLoanFiltersAndSearch = user.hasFeatures(SubscriptionFeature.LoanFiltersAndSearch);

        if (searchString) {
            yield call(restoreFiltersFromQueryString, searchString);
        } else if (hasLoanFiltersAndSearch) { // Check for default saved filter
            const defaultFilter: UserConfigFilter | undefined = yield select((s: AppState) =>
                s.entities.pageConfig.configs?.[PageConfigType.LoanMonitor]?.filters?.find(f => f.default)
            );

            if (defaultFilter) {
                yield put(filterActions.selectFilterFromConfig(defaultFilter));
            }
        }

        yield put(loanActions.search());
    } catch (e) {
        yield put(errorActions.unexpectedError(e));
    }
}

function* watchSearch() {
    try {
        const filter: TFilter<BwicFilterType.LoanMonitor> = yield select((s: AppState) => s.filters.loanMonitor.filter);
        yield call(history.replace, {
            ...history.location,
            state: new LocationStateBuilder(history.location.state as LocationState).resetPanel().result(),
        });
        yield put(loanActions.resetLoans());
        yield put(loanActions.request());

        const { currentPageNumber, pageSize, issuersSearch, sortingField, sortDirection }: LoanState = yield select((s: AppState) => s.loan);
        const params: LoanSearchParams = {
            ...getFilterParams(filter as CommonFilter),
            issuers: issuersSearch.length ? issuersSearch : undefined,
            page: currentPageNumber,
            pageSize,
            orderByColumn: sortingField,
            sortOrder: sortDirection,
        };

        const { result, totalRecordNumber }: PaginationResult<Loan> = yield call(loanService.searchLoans, params);

        yield put(loanActions.requestSuccess(result, totalRecordNumber));
        yield put(filterActions.applyFilter());
        yield call(redirectWithFilterArguments);
    } catch (e) {
        if (errorActions.isRequestCancelationError(e)) {
            yield put(loanActions.reset());
        } else {
            yield put(loanActions.requestError());
            yield put(errorActions.criticalError(e));
        }
    }
}

function* watchLoadNextPage() {
    try {
        const { requestState, nextPageRequestState, totalRecordNumber, currentPageNumber, pageSize }: LoanState =
            yield select((s: AppState) => s.loan);

        if (isRequesting(requestState, nextPageRequestState) || currentPageNumber * pageSize >= totalRecordNumber) return;

        const lastAppliedFilter: CommonFilter = yield select((s: AppState) => s.filters.loanMonitor.lastAppliedFilter);

        yield put(loanActions.nextPageRequest());

        const { currentPageNumber: nextPage, issuersSearch, sortingField, sortDirection }: LoanState = yield select((s: AppState) => s.loan);
        const params: LoanSearchParams = {
            ...getFilterParams(lastAppliedFilter),
            issuers: issuersSearch.length ? issuersSearch : undefined,
            page: nextPage,
            pageSize,
            orderByColumn: sortingField,
            sortOrder: sortDirection,
        };

        const { result, totalRecordNumber: total }: PaginationResult<Loan> = yield call(loanService.searchLoans, params);

        yield put(loanActions.nextPageRequestSuccess(result, total));
    } catch (e) {
        if (errorActions.isRequestCancelationError(e)) {
            yield put(loanActions.reset());
        } else {
            yield put(loanActions.nextPageRequestError());
            yield put(errorActions.unexpectedError(e));
        }
    }
}

function* restoreFiltersFromQueryString(searchString: string) {
    const arrangerItems = (yield select((s: AppState) => Object.keys(s.entities.arrangers.items))) as string[];
    const collateralManagerItems = (yield select((s: AppState) => Object.keys(s.entities.collateralManagers.items))) as string[];
    const debtInvestorItems = (yield select((s: AppState) => Object.keys(s.entities.debtInvestors.items))) as string[];
    const analystItems = (yield select((s: AppState) => Object.keys(s.entities.cloManagerUsers.items))) as string[];
    const issuerItems = (yield select((s: AppState) => s.entities.loanFieldValues.values[LoanField.Issuer])) as string[];
    const industryItems = (yield select((s: AppState) => s.entities.loanFieldValues.values[LoanField.Industry])) as string[];
    const useOfProceedsItems = (yield select((s: AppState) => s.entities.loanFieldValues.values[LoanField.UseOfProceeds])) as string[];
    const refIndexItems = (yield select((s: AppState) => s.entities.loanFieldValues.values[LoanField.RefIndex])) as string[];

    let statusOptions: string[] = [];
    let reviewOptions: string[] = [];
    let currencyOptions: string[] = [];
    let analystValues: string[] = [];
    let arrangerValues: string[] = [];
    let coArrangerValues: string[] = [];
    let pricingFrom: Date | null = null;
    let pricingTo: Date | null = null;
    let covLiteValue: boolean | undefined = undefined;
    let dataroomValues: string[] = [];
    let industryValues: string[] = [];
    let useOfProceedsValues: string[] = [];
    let issuerValues: string[] = [];
    let maturityFrom: Date | null = null;
    let maturityTo: Date | null = null;
    let refIndexValue: string | undefined = undefined;
    let ratingsOptions: string[] = [];
    let cfRatingsOptions: string[] = [];
    let dueFrom: Date | null = null;
    let dueTo: Date | null = null;

    const [termSerializers, getTermDeserializeResult] = numericRange('termFrom', 'termTo');

    const hasLoanFullDetails = user.hasFeatures(SubscriptionFeature.LoanFullDetails);
    const isCollateralManager = user.hasRoles(roles.CollateralManager);
    const acceptedStatuses = hasLoanFullDetails
        ? Object.values(LoanStatus)
        : [LoanStatus.Active];

    const arrangerAcceptedValues = arrangerItems.concat(collateralManagerItems, debtInvestorItems);

    const serializers = new ListBuilder<QueryStringArgumentSerializer<any>>()
        .add(
            loanCurrencies(currencies => currencyOptions = currencies),
            arrangers(values => arrangerValues = values, arrangerAcceptedValues),
        )
        .addWhen(() => isCollateralManager,
            loanReviewStatuses(options => reviewOptions = options),
            loanAnalysts(values => analystValues = values, analystItems),
        )
        .addWhen(() => hasLoanFullDetails,
            loanStatuses(options => statusOptions = options, acceptedStatuses),
            arrangers(values => coArrangerValues = values, arrangerAcceptedValues, 'coArranger'),
            dateFrom(value => pricingFrom = value, 'pricingFrom'),
            dateTo(value => pricingTo = value, 'pricingTo'),
            loanCovLite(value => covLiteValue = value),
            loanDataroom(values => dataroomValues = values),
            loanIndustries(values => industryValues = values, industryItems),
            loanUseOfProceeds(values => useOfProceedsValues = values, useOfProceedsItems),
            loanIssuers(values => issuerValues = values, issuerItems),
            dateFrom(value => maturityFrom = value, 'maturityFrom'),
            dateTo(value => maturityTo = value, 'maturityTo'),
            loanRefIndex(value => refIndexValue = value, 'refIndex', refIndexItems),
            ...termSerializers,
            ratings(options => ratingsOptions = options),
            ratings(options => cfRatingsOptions = options, undefined, 'cfRatings'),
            loanDueFrom(value => dueFrom = value),
            loanDueTo(value => dueTo = value),
        )
        .result();

    queryStringSerializer.deserialize(searchString, serializers);

    yield put(filterActions.resetFilter());

    const termDeserializeResult = getTermDeserializeResult();

    const statusesCommand = {
        shouldExecute: () => statusOptions.length > 0,
        execute: function* () {
            yield all(statusOptions.map(s => put(filterActions.filterSelectChange(s, 'statuses'))));
        },
    };
    const reviewCommand = {
        shouldExecute: () => reviewOptions.length > 0,
        execute: function* () {
            yield all(reviewOptions.map(r => put(filterActions.filterSelectChange(r, 'review'))));
        },
    };
    const currenciesCommand = {
        shouldExecute: () => currencyOptions.length > 0,
        execute: function* () {
            yield all(currencyOptions.map(c => put(filterActions.filterSelectChange(c as LoanCurrency, 'currency'))));
        },
    };
    const analystCommand = {
        shouldExecute: () => analystValues.length > 0,
        execute: function* () {
            yield all(analystValues.map(v => put(filterActions.filterSelectChange(v, 'analyst'))));
        },
    };
    const arrangerCommand = {
        shouldExecute: () => arrangerValues.length > 0,
        execute: function* () {
            yield all(arrangerValues.map(a => put(filterActions.filterSelectChange(a, 'arranger'))));
        },
    };
    const coArrangerCommand = {
        shouldExecute: () => coArrangerValues.length > 0,
        execute: function* () {
            yield put(filterActions.filterVisibilityChange('coArranger'));
            yield all(coArrangerValues.map(a => put(filterActions.filterSelectChange(a, 'coArranger'))));
        },
    };
    const pricingCommand = {
        shouldExecute: () => !isNil(pricingFrom) || !isNil(pricingTo),
        execute: function* () {
            yield put(filterActions.makeFilterVisible('pricing'));
            const option = getDateOptionByRange(dateRangeFilterOptions.ThisWeek, pricingFrom, pricingTo);
            yield put(filterActions.filterDateSelectOption(option, 'pricing'));
            yield put(filterActions.filterDateSelectCustomRange({ from: pricingFrom, to: pricingTo }, 'pricing'));
        },
    };
    const covLiteCommand = {
        shouldExecute: () => !isNil(covLiteValue),
        execute: function* () {
            yield put(filterActions.filterVisibilityChange('covLite'));
            yield put(filterActions.filterRadioChange(covLiteValue!, 'covLite'));
        },
    };
    const dataroomCommand = {
        shouldExecute: () => dataroomValues.length > 0,
        execute: function* () {
            yield put(filterActions.filterVisibilityChange('dataroom'));
            yield all(dataroomValues.map(v => put(filterActions.filterSelectChange(v, 'dataroom'))));
        },
    };
    const industryCommand = {
        shouldExecute: () => industryValues.length > 0,
        execute: function* () {
            yield put(filterActions.filterVisibilityChange('industry'));
            yield all(industryValues.map(v => put(filterActions.filterSelectChange(v, 'industry'))));
        },
    };
    const useOfProceedsCommand = {
        shouldExecute: () => useOfProceedsValues.length > 0,
        execute: function* () {
            yield put(filterActions.filterVisibilityChange('useOfProceeds'));
            yield all(useOfProceedsValues.map(v => put(filterActions.filterSelectChange(v, 'useOfProceeds'))));
        },
    };
    const issuersCommand = {
        shouldExecute: () => issuerValues.length > 0,
        execute: function* () {
            yield put(loanActions.issuerSearchChange(issuerValues));
        },
    };
    const maturityCommand = {
        shouldExecute: () => !isNil(maturityFrom) || !isNil(maturityTo),
        execute: function* () {
            yield put(filterActions.makeFilterVisible('maturity'));
            const option = getDateOptionByRange(dateRangeFilterOptions.ThisWeek, maturityFrom, maturityTo);
            yield put(filterActions.filterDateSelectOption(option, 'maturity'));
            yield put(filterActions.filterDateSelectCustomRange({ from: maturityFrom, to: maturityTo }, 'maturity'));
        },
    };
    const refIndexCommand = {
        shouldExecute: () => !isNil(refIndexValue),
        execute: function* () {
            yield put(filterActions.makeFilterVisible('refIndex'));
            yield put(filterActions.filterRadioChange(refIndexValue!, 'refIndex'));
        },
    };
    const termCommand = {
        shouldExecute: () => !!termDeserializeResult?.numeric,
        execute: function* () {
            yield put(filterActions.filterDateQueryStringDeserialized('term', termDeserializeResult));
        },
    };
    const ratingsCommand = {
        shouldExecute: () => ratingsOptions.length > 0,
        execute: function* () {
            yield put(filterActions.filterVisibilityChange('ratings'));
            yield all(ratingsOptions.map(r => put(filterActions.filterSelectChange(r, 'ratings'))));
        },
    };
    const cfRatingsCommand = {
        shouldExecute: () => cfRatingsOptions.length > 0,
        execute: function* () {
            yield put(filterActions.filterVisibilityChange('cfRatings'));
            yield all(cfRatingsOptions.map(r => put(filterActions.filterSelectChange(r, 'cfRatings'))));
        },
    };
    const dueCommand = {
        shouldExecute: () => !isNil(dueFrom) || !isNil(dueTo),
        execute: function* () {
            const selectedOption = getDateTimeOptionByRange(dueFrom, dueTo);
            if (!selectedOption) return;

            yield put(filterActions.makeFilterVisible('due'));
            yield put(filterActions.filterDateTimeOptionSelected(selectedOption, 'due'));

            const timeOptions = getTimeOptionsFromRange(dueFrom, dueTo);
            const range: DateTimeRange = {
                from: dueFrom ? { date: dueFrom, timeOption: timeOptions.from } : undefined,
                to: dueTo ? { date: dueTo, timeOption: timeOptions.to } : undefined,
            };
            yield put(filterActions.filterDateTimeSelectCustomRange(range, 'due'));
        },
    };

    yield new DeserializeCommandHandler()
        .addCommand(statusesCommand)
        .addCommand(reviewCommand)
        .addCommand(currenciesCommand)
        .addCommand(analystCommand)
        .addCommand(arrangerCommand)
        .addCommand(coArrangerCommand)
        .addCommand(pricingCommand)
        .addCommand(covLiteCommand)
        .addCommand(dataroomCommand)
        .addCommand(industryCommand)
        .addCommand(useOfProceedsCommand)
        .addCommand(issuersCommand)
        .addCommand(maturityCommand)
        .addCommand(refIndexCommand)
        .addCommand(termCommand)
        .addCommand(ratingsCommand)
        .addCommand(cfRatingsCommand)
        .addCommand(dueCommand)
        .processGenerator();
}

function* storeSelectFilter<T>(
    filterName: string,
    items: T[],
    getValue: (item: T) => string,
    getText: (item: T) => string,
) {
    const { filter, initialFilter }: AppState['filters']['loanMonitor'] = yield select((s: AppState) => s.filters.loanMonitor);

    const buildOptions = (existing: FilterSelectGroup<string> | undefined) => {
        const selectedValues = new Set(
            existing?.filter.filter(o => o.selected).map(o => o.value) ?? [],
        );

        return items
            .map(item => {
                const value = getValue(item);
                return { selected: selectedValues.has(value), disabled: false, visible: true, value, text: getText(item) };
            })
            .sort((a, b) => a.text.localeCompare(b.text));
    };

    yield put(filterActions.storeFilterData.success({
        filterName,
        filterType: BwicFilterType.LoanMonitor,
        data: buildOptions(filter[filterName] as FilterSelectGroup<string>),
        initialData: initialFilter && buildOptions(initialFilter[filterName] as FilterSelectGroup<string>),
    }));
}

function* storeRadioFilter<T>(
    filterName: string,
    items: T[],
    getValue: (item: T) => string,
    getText: (item: T) => string,
) {
    const { filter, initialFilter }: AppState['filters']['loanMonitor'] = yield select((s: AppState) => s.filters.loanMonitor);

    const buildData = (existing: FilterRadioGroup<string> | undefined) => ({
        selectedOption: existing?.filter.selectedOption ?? null,
        options: items.map(item => ({
            selected: false,
            disabled: false,
            visible: true,
            value: getValue(item),
            text: getText(item),
        })),
    }) as unknown as unknown[];

    yield put(filterActions.storeFilterData.success({
        filterName,
        filterType: BwicFilterType.LoanMonitor,
        data: buildData(filter[filterName] as FilterRadioGroup<string>),
        initialData: initialFilter && buildData(initialFilter[filterName] as FilterRadioGroup<string>),
    }));
}

function* watchCloManagerUsersLoaded({ payload }: ReturnType<typeof cloManagerUsersActions.response>) {
    const companyId = user.current()?.companyId;
    const users = companyId == null ? payload.users : payload.users.filter(u => u.companyId === companyId);
    yield* storeSelectFilter('analyst', users, u => String(u.id), u => `${u.firstName} ${u.lastName}`);
}

function* watchLoanFieldValuesLoaded({ payload }: ReturnType<typeof loanFieldValuesActions.response>) {
    yield* storeSelectFilter('industry', payload.values.industries, v => v, v => v);
    yield* storeSelectFilter('useOfProceeds', payload.values.useOfProceeds, v => v, v => v);
    yield* storeRadioFilter('refIndex', payload.values.refIndexes, v => v, v => v);
}

export function* watchHideArrangersWithoutLoans() {
    const arrangersLoaded: boolean = yield select((s: AppState) => isRequestSuccess(s.entities.arrangers.requestState));
    const collateralManagersLoaded: boolean = yield select((s: AppState) => isRequestSuccess(s.entities.collateralManagers.requestState));
    const debtInvestorsLoaded: boolean = yield select((s: AppState) => isRequestSuccess(s.entities.debtInvestors.requestState));
    const loanFieldValuesLoaded: boolean = yield select((s: AppState) => isRequestSuccess(s.entities.loanFieldValues.requestState));

    if (!arrangersLoaded || !collateralManagersLoaded || !debtInvestorsLoaded || !loanFieldValuesLoaded) {
        return;
    }

    const arrangersList: Company[] = yield select((s: AppState) => Object.values(s.entities.arrangers.items));
    const collateralManagersList: Company[] = yield select((s: AppState) => Object.values(s.entities.collateralManagers.items));
    const debtInvestorsList: Company[] = yield select((s: AppState) => Object.values(s.entities.debtInvestors.items));
    const values: LoanFieldValues = yield select((s: AppState) => s.entities.loanFieldValues.values);
    const isAdmin = user.hasRoles(roles.Administrator);


    const arrangerReferenceNames = new Set([
        ...values[LoanField.Arranger] ?? [],
        ...(isAdmin ? values[LoanField.DraftArranger] ?? [] : []),
    ]);
    const coArrangerReferenceNames = new Set([
        ...values[LoanField.CoArranger] ?? [],
        ...(isAdmin ? values[LoanField.DraftCoArranger] ?? [] : []),
    ]);

    const combinedCompanies = arrangersList.concat(collateralManagersList, debtInvestorsList);
    // Dedupe by referenceName in case a company holds more than one role
    const uniqueCompanies = Array.from(
        new Map(combinedCompanies.map(a => [a.referenceName, a])).values(),
    );

    const arrangerOptions = uniqueCompanies.filter(a => arrangerReferenceNames.has(a.referenceName));
    const coArrangerOptions = uniqueCompanies.filter(a => coArrangerReferenceNames.has(a.referenceName));

    yield* storeSelectFilter('arranger', arrangerOptions, a => a.referenceName, a => a.legalName);
    yield* storeSelectFilter('coArranger', coArrangerOptions, a => a.referenceName, a => a.legalName);
}

function* watchDeleteLoan({ payload }: ReturnType<typeof loanActions.deleteLoan>) {
    try {
        yield call(loanService.deleteLoan, payload.id);
        yield put(loanActions.deleteLoanSuccess(payload.id));
        yield put(loanFieldValuesActions.reset());
    } catch (e) {
        yield put(errorActions.unexpectedError(e));
    }
}

function* watchAdminLoanSave({ payload }: ReturnType<typeof loanActions.adminLoanSave>) {
    try {
        const loan: Loan = payload.id
            ? yield call(loanService.updateLoan, payload.id, payload.model)
            : yield call(loanService.createLoan, payload.model);
        yield put(loanActions.saveLoanSuccess(loan));
        yield put(loanFieldValuesActions.reset());
        yield put(loanActions.resetLoans());
        yield call(history.push, routes.loanMonitor);
    } catch (e) {
        yield put(loanActions.saveLoanError());
        yield put(errorActions.unexpectedError(e));
    }
}

function* watchExportLoans() {
    try {
        const filter: CommonFilter = yield select((s: AppState) => s.filters.loanMonitor.lastAppliedFilter);
        const { issuersSearch, sortingField, sortDirection }: LoanState = yield select((s: AppState) => s.loan);

        const params: LoanSearchParams = {
            ...getFilterParams(filter),
            issuers: issuersSearch.length ? issuersSearch : undefined,
            orderByColumn: sortingField,
            sortOrder: sortDirection,
        };

        const file: { blob: Blob; name: string } = yield call(loanService.exportLoans, params);
        saveAs(file.blob, file.name);
        yield put(loanActions.exportLoansSuccess());
    } catch (e) {
        yield put(loanActions.exportLoansError());
        yield put(errorActions.unexpectedError(e));
    }
}

function* watchLoanUpdated({ payload }: ReturnType<typeof loanActions.loanUpdated>) {
    try {
        yield call(loanService.editPopup, payload.id, payload.model);
        const existing: Loan | undefined = yield select((s: AppState) => s.loan.loans.find(l => l.id === payload.id));

        const updated = existing ? {
            ...existing,
            analystUserId: payload.model.analystUserId ?? undefined,
            review: payload.model.review ?? undefined,
            order: payload.model.order ?? undefined,
            target: payload.model.target ?? undefined,
            allocation: payload.model.allocation ?? undefined,
            allocationPct: payload.model.allocationPct ?? undefined,
            otherPassingReasons: payload.model.otherPassingReasons ?? undefined,
            passingReasons: payload.model.passingReasons ?? undefined,
        } : undefined;

        yield call(history.replace, {
            ...history.location,
            state: new LocationStateBuilder(history.location.state as LocationState).resetPanel().result(),
        });
        yield put(loanActions.loanUpdatedSuccess(updated));
    } catch (e) {
        yield put(loanActions.loanUpdatedError());
        yield put(errorActions.unexpectedError(e));
    }
}

export function* watchLoan() {
    yield takeLatest(getType(loanActions.init), watchInit);
    yield takeLatest(getType(loanActions.search), watchSearch);
    yield takeEvery(getType(loanActions.loadNextPage), watchLoadNextPage);
    yield takeLatest(getType(loanActions.deleteLoan), watchDeleteLoan);
    yield takeLatest(getType(loanActions.loanUpdated), watchLoanUpdated);
    yield takeLatest(getType(loanActions.adminLoanSave), watchAdminLoanSave);
    yield takeLatest(getType(loanActions.exportLoans), watchExportLoans);
    yield takeLatest(getType(cloManagerUsersActions.response), watchCloManagerUsersLoaded);
    yield takeLatest(getType(loanFieldValuesActions.response), watchLoanFieldValuesLoaded);
    yield takeLatest(
        [getType(arrangersActions.response), getType(collateralManagersActions.responseCollateralManagers), getType(debtInvestorsActions.responseDebtInvestors), getType(loanFieldValuesActions.response)],
        watchHideArrangersWithoutLoans,
    );
    yield takeLatest(getType(loanActions.restoreFilterQueryString), watchRestoreFiltersQueryString);
}
